import {
  IImageAssetPort,
  ImageLoadRequestDTO,
  ImageLoadResultDTO,
  PresetItemDTO,
} from '../ports/types';
import { ImageLoader, ImageLoaderOptions, LoadedRawImage } from './ImageLoader';
import { AspectRatioFitter, CanvasFitLayout } from './AspectRatioFitter';
import { PresetCatalog } from './PresetCatalog';

export interface ImageAssetServiceOptions {
  loaderOptions?: ImageLoaderOptions;
}

/**
 * 画像・アセット管理ユニット統合ファサードサービス (ImageAssetService)
 * IImageAssetPort を完全実装し、ImageLoader, AspectRatioFitter,
 * PresetCatalog を協調動作させるとともに、旧アセットの安全なメモリ解放を管理する。
 */
export class ImageAssetService implements IImageAssetPort {
  private loader: ImageLoader;
  private fitter: AspectRatioFitter;
  private catalog: PresetCatalog;

  private currentRawAsset: LoadedRawImage | null = null;

  constructor(options?: ImageAssetServiceOptions) {
    this.loader = new ImageLoader(options?.loaderOptions);
    this.fitter = new AspectRatioFitter();
    this.catalog = new PresetCatalog();
  }

  // ==========================================
  // IImageAssetPort 実装
  // ==========================================

  /**
   * ユーザー画像またはプリセット画像を読み込む
   */
  public async loadImage(request: ImageLoadRequestDTO): Promise<ImageLoadResultDTO> {
    // 1. 直前の画像アセットのオブジェクトURL・メモリを自動解放
    this.disposeCurrentAsset();

    // 2. 画像デコード＆バリデーション
    const raw = await this.loader.load(request);
    this.currentRawAsset = raw;

    // 3. アスペクト比計算
    const aspectRatio = this.fitter.calculateAspectRatio(raw.naturalWidth, raw.naturalHeight);

    return {
      assetId: raw.assetId,
      imageSource: raw.imageSource,
      width: raw.naturalWidth,
      height: raw.naturalHeight,
      aspectRatio,
    };
  }

  /**
   * 利用可能なプリセットサンプル一覧を取得
   */
  public getPresetList(): PresetItemDTO[] {
    return this.catalog.getPresetList();
  }

  /**
   * 指定されたプリセット画像を読み込む (未知IDは既定プリンへフォールバック)
   */
  public async loadPresetById(presetId: string): Promise<ImageLoadResultDTO> {
    const preset = this.catalog.getPresetById(presetId);
    return this.loadImage({
      source: preset.svgDataUri,
      sourceType: 'preset',
    });
  }

  // ==========================================
  // 補助・統合ユーティリティメソッド
  // ==========================================

  /**
   * 現在読み込まれている画像に対するキャンバスフィッティング幾何計算
   */
  public calculateCanvasFit(canvasWidth: number, canvasHeight: number, paddingRatio: number = 0.90): CanvasFitLayout | null {
    if (!this.currentRawAsset) {
      return null;
    }
    return this.fitter.calculateFit(
      this.currentRawAsset.naturalWidth,
      this.currentRawAsset.naturalHeight,
      canvasWidth,
      canvasHeight,
      paddingRatio
    );
  }

  /**
   * 現在保持しているアセットのメモリを解放
   */
  public disposeCurrentAsset(): void {
    if (this.currentRawAsset) {
      this.currentRawAsset.dispose();
      this.currentRawAsset = null;
    }
  }

  /**
   * サービス全体の破棄
   */
  public dispose(): void {
    this.disposeCurrentAsset();
    this.loader.disposeCurrent();
  }

  public getCurrentAsset(): LoadedRawImage | null {
    return this.currentRawAsset;
  }

  public getLoader(): ImageLoader {
    return this.loader;
  }

  public getFitter(): AspectRatioFitter {
    return this.fitter;
  }

  public getCatalog(): PresetCatalog {
    return this.catalog;
  }
}
